import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '../../../../shared';
import { Property } from '../../shared/models';
import { CollectionFrequency } from 'app/shared';

@Component({
  selector: 'sp-add-property-financials',
  templateUrl: './add-property-financials.html',
  standalone: false
})
export class AddPropertyFinancialsComponent implements OnInit {

  @Input() formGroup: UntypedFormGroup;
  @Input() property: Property;
  @Input() months: string[];
  @Input() collectionFrequencies: CollectionFrequency[];

  fiscalYearStartDates: Array<string>;
  startingFiscalYearsOptions: any = [];

  currentYear = new Date().getFullYear();

  constructor(private fb: UntypedFormBuilder) { }

  ngOnInit() {
    this.fiscalYearStartDates = this.getFiscalStartDates();
    this.startingFiscalYearsOptions = this.buildStartingFiscalYearDropdown();

    this.initForm();
  }

  resetForm() {

    this.formGroup.reset();
    this.formGroup.controls['fiscalYearStartMonth'].setValue(this.property.propertyFinancial.fiscalYearStartMonth);
    this.formGroup.controls['startingFiscalYear'].setValue(this.currentYear);
    this.formGroup.controls['feeCollectionFrequencyId'].setValue(this.property.propertyFinancial.feeCollectionFrequencyId);
    this.formGroup.controls['reserveMonthStart'].setValue(this.property.propertyFinancial.reserveMonthStart);
    this.formGroup.controls['hasExpensesYesNo'].setValue(false);
  }

  private initForm() {

    this.formGroup.addControl(
      'fiscalYearStartMonth',
      this.fb.control(this.property.propertyFinancial.fiscalYearStartMonth,
        [
          Validators.required,
        ])
    );

    this.formGroup.addControl(
      'startingFiscalYear',
      this.fb.control(this.currentYear,
        [
          Validators.required,
        ])
    );

    this.formGroup.addControl(
      'averageHoaAnnualFee',
      this.fb.control(this.property.propertyFinancial.averageHoaAnnualFee,
        [
          Validators.required,
          CustomValidators.rangeValidator(0, 99000000.00, null, true)
        ])
    );
    this.formGroup.controls['averageHoaAnnualFee'].valueChanges.subscribe(() => {
      this.setAnnualReserveContributionValidation();
    });

    this.formGroup.addControl(
      'feeCollectionFrequencyId',
      this.fb.control(this.property.propertyFinancial.feeCollectionFrequencyId,
        [
          Validators.required,
        ])
    );

    this.formGroup.addControl(
      'annualReserveContribution',
      this.fb.control(this.property.propertyFinancial.annualReserveContribution,
        [
          Validators.required,
          CustomValidators.naturalNumber,
        ])
    );
    this.formGroup.controls['annualReserveContribution'].valueChanges.subscribe(() => {
      this.setUpcomingExpensesValidators();
    });

    this.formGroup.addControl(
      'yearEndReserveBalance',
      this.fb.control(this.property.propertyFinancial.yearEndReserveBalance,
        [
          Validators.required,
          CustomValidators.rangeValidator(0, 99000000.00, null, true)
        ])
    );
    this.formGroup.controls['yearEndReserveBalance'].valueChanges.subscribe(() => {
      this.setUpcomingExpensesValidators();
    });

    this.formGroup.addControl(
      'reserveMonthStart',
      this.fb.control(this.property.propertyFinancial.reserveMonthStart,
        [
          Validators.required,
        ])
    );

    this.formGroup.addControl(
      'hasExpensesYesNo',
      this.fb.control(false)
    );
    this.formGroup.controls['hasExpensesYesNo'].valueChanges.subscribe(() => {
      this.setUpcomingExpensesValidators();
    });

    this.formGroup.addControl(
      'upcomingExpenses',
      this.fb.control(this.property.propertyFinancial.upcomingExpenses,
        [
          Validators.required,
        ])
    );

    this.formGroup.addControl(
      'inflationRate',
      this.fb.control(this.property.propertyFinancial.inflationRate,
        [
          Validators.required,
          CustomValidators.rangeValidator(0, 99.99,
            (min, max) => `Inflation Rate should be from ${min}% to ${max}%.`)
        ])
    );

    this.formGroup.addControl(
      'interestRate',
      this.fb.control(this.property.propertyFinancial.interestRate,
        [
          Validators.required,
          CustomValidators.rangeValidator(0, 99.99,
            (min, max) => `Interest Rate should be from ${min}% to ${max}%.`)
        ])
    );

    this.formGroup.addControl(
      'reserveContributionIncreaseRate',
      this.fb.control(this.property.propertyFinancial.reserveContributionIncreaseRate,
        [
          Validators.required,
          CustomValidators.rangeValidator(0, 99.99,
            (min, max) => `Reserve Contribution Increase Rate should be from ${min}% to ${max}%.`)
        ])
    );

    // Reserve Study fields
    this.formGroup.addControl('reserveStudyDate', this.fb.control('', Validators.compose([
      CustomValidators.dateValidator,
      CustomValidators.notEmptyStringValidator,
    ])));

    this.formGroup.addControl('reserveStudyTypeId', this.fb.control('', Validators.compose([])));

    this.formGroup.addControl('reserveStudyProviderName', this.fb.control('', Validators.compose([
      CustomValidators.notEmptyStringValidator,
    ])));
  }

  private computeAnnualReserveContributionLimit() {

    const averageHoaAnnualFee = this.formGroup.controls['averageHoaAnnualFee'];
    return !this.fieldHasNoValidValue(averageHoaAnnualFee)
      ? parseFloat(averageHoaAnnualFee.value.toString())
      : null;
  }

  private computeUpcomingExpensesLimit() {

    if (this.fieldHasNoValidValue(this.formGroup.controls['annualReserveContribution'])
      || this.fieldHasNoValidValue(this.formGroup.controls['reserveMonthStart'])
      || this.fieldHasNoValidValue(this.formGroup.controls['fiscalYearStartMonth'])
      || this.fieldHasNoValidValue(this.formGroup.controls['yearEndReserveBalance'])) {
      return null;
    }

    const annualReserveContribution = this.getNumberValue(this.formGroup.controls['annualReserveContribution'].value, false);
    const reserveMonthStart = this.getNumberValue(this.formGroup.controls['reserveMonthStart'].value, true);
    const fiscalYearStartMonth = this.getNumberValue(this.formGroup.controls['fiscalYearStartMonth'].value, true);
    const yearEndReserveBalance = this.getNumberValue(this.formGroup.controls['yearEndReserveBalance'].value, false);

    let remainingMonthsTillNextFiscal = 0;
    const monthlyReserveContribution = annualReserveContribution / 12;
    if (fiscalYearStartMonth === reserveMonthStart) {
      remainingMonthsTillNextFiscal = 0;
    } else if (fiscalYearStartMonth < reserveMonthStart) {
      remainingMonthsTillNextFiscal = 12 - reserveMonthStart + fiscalYearStartMonth;
    } else {
      remainingMonthsTillNextFiscal = fiscalYearStartMonth - reserveMonthStart;
    }

    const reserveContributionTillNextFiscalYear = monthlyReserveContribution * remainingMonthsTillNextFiscal;

    return reserveContributionTillNextFiscalYear + yearEndReserveBalance;
  }

  private fieldHasNoValidValue(field) {
    return field == null ||
      field.valid === false ||
      field.value == null ||
      field.value.toString() === '';
  }

  private getFiscalStartDates(): Array<string> {
    const months = [];
    for (let i = 0; i < this.months.length; i++) {
      months.push(this.months[i] + ' 1');
    }
    return months;
  }

  private buildStartingFiscalYearDropdown() {
    const years = [];

    for (let i = 15; i >= 0; i--) {
      const year = this.currentYear - i;
      years.push(year);
    }

    return years;
  }

  private getNumberValue(value: string, toInt: boolean, defaultValue = null) {
    let num = null;
    if (toInt) {
      num = parseInt(value, 10);
      return !isNaN(num) && num != null && num !== undefined ? num : defaultValue;
    } else {
      num = parseFloat(value);
      return !isNaN(num) && num != null && num !== undefined ? num : defaultValue;
    }
  }

  private setAnnualReserveContributionValidation() {

    const annualReserveContribution = this.formGroup.controls['annualReserveContribution'];

    annualReserveContribution.clearValidators();

    let limit = this.computeAnnualReserveContributionLimit();

    let message = null;
    if (limit == null) {
      limit = 99000000;
    } else {

      if (limit === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        message = (min, max) => 'Annual Reserve Contributions should be $0.';
      }
    }

    annualReserveContribution.setValidators(
      Validators.compose([
        Validators.required,
        CustomValidators.rangeValidator(0, limit, message, true)
      ]));

    annualReserveContribution.updateValueAndValidity();
  }

  private setUpcomingExpensesValidators() {

    const upcomingExpenses = this.formGroup.controls['upcomingExpenses'];
    const hasExpensesYesNo = this.formGroup.controls['hasExpensesYesNo'];

    upcomingExpenses.clearValidators();

    if (hasExpensesYesNo.value === false) {
      upcomingExpenses.reset();
      return;
    }

    let limit = this.computeUpcomingExpensesLimit();
    if (limit != null) {
      limit = limit > 50000000 ? 50000000 : limit;
    } else {
      limit = 50000000;
    }

    upcomingExpenses.setValidators(
      Validators.compose([
        Validators.required,
        CustomValidators.rangeValidator(0, limit, null, true)
      ]));

    upcomingExpenses.updateValueAndValidity();
  }
}
